import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { rbac } from '@dha-nexus/shared';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import {
  DateInput,
  EmptyState,
  ErrorState,
  FormField,
  KpiHeroBand,
  KpiBandSkeleton,
  LoadingSkeleton,
  NumberInput,
  PageHeader,
  Select,
  StatusChip,
  StatusDonutMini,
  InlineScoreCell,
  TrendBars,
  BrandedEmptyState,
  Textarea,
  TextInput,
  toast,
} from '../../components/index.js';
import { usePeople } from '../../lib/usePeople.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import {
  deriveThisWeekChecklist,
  enumerateWeekDays,
  pickCoveringTimesheet,
  pickCurrentTimesheet,
  sortTimesheetsByPeriodDescending,
  timeTargetRoute,
  todayDateOnly,
} from './timeHelpers.js';
import './time.css';
import './timesheets.redesign.css';

// Spoken equivalent of each step's visual state, so done/active/todo is conveyed to assistive
// tech independent of color and the aria-hidden index glyph.
const STEP_STATE_WORD = { done: 'completed', active: 'current step', todo: 'not started' };

// Compact "This week" task strip — Start -> Log -> Review -> Submit, reflecting live state.
// Scoped to the submitter's own workflow (no approve/reject step). Rendered above the KPI band.
function ThisWeekStrip({ steps, periodLabel }) {
  return (
    <section className="ts-weekstrip" aria-label="This week tasks">
      <div className="ts-weekstrip__head">
        <span className="eyebrow">This week</span>
        {periodLabel ? <span className="ts-weekstrip__period muted">{periodLabel}</span> : null}
      </div>
      <ol className="ts-weekstrip__steps">
        {steps.map((step, index) => (
          <li key={step.key} className="ts-weekstrip__step" data-state={step.state}>
            <span className="ts-weekstrip__index" aria-hidden>
              {step.state === 'done' ? '✓' : index + 1}
            </span>
            <span className="ts-weekstrip__body">
              <span className="ts-weekstrip__label">{step.label}</span>
              <span className="ts-weekstrip__srstate">
                {STEP_STATE_WORD[step.state] || step.state}
              </span>
              <span className="ts-weekstrip__hint muted">{step.hint}</span>
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function entriesForTimesheet(entries = [], timesheetId) {
  return entries.filter((entry) => entry.timesheetId === timesheetId);
}

// Sum logged hours across a set of time entries (defensive against bad numbers).
function sumHours(entries = []) {
  return entries.reduce((total, entry) => {
    const value = Number(entry.hours);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
}

// Round to a stable display value (avoid 6.0000001 artifacts from float adds).
function fmtHours(value) {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

// Map a timesheet/entry workflow status to an executive-traffic-light tone.
function statusTone(status) {
  switch (String(status || '').toLowerCase()) {
    case 'approved':
      return 'sage';
    case 'rejected':
      return 'brick';
    case 'submitted':
    case 'resubmitted':
      return 'amber';
    case 'draft':
      return 'blue';
    default:
      return 'slate';
  }
}

// Roll a set of timesheets into approval-status donut segments. Every row lands
// in exactly one bucket; zero-count buckets are filtered out by StatusDonutMini.
function approvalSegments(rows = []) {
  const order = [
    { key: 'approved', label: 'Approved', tone: 'sage' },
    { key: 'submitted', label: 'In review', tone: 'amber' },
    { key: 'resubmitted', label: 'In review', tone: 'amber' },
    { key: 'rejected', label: 'Rejected', tone: 'brick' },
    { key: 'draft', label: 'Draft', tone: 'blue' },
  ];
  const counts = new Map();
  for (const row of rows) {
    const match = order.find((o) => o.key === String(row.status || '').toLowerCase()) || {
      label: 'Other',
      tone: 'slate',
    };
    const prev = counts.get(match.label) || { label: match.label, count: 0, tone: match.tone };
    prev.count += 1;
    counts.set(match.label, prev);
  }
  return [...counts.values()];
}

// Build an ordered per-date hours series from a set of entries (for MiniBars).
function dailyHoursSeries(entries = []) {
  const byDate = new Map();
  for (const entry of entries) {
    const value = Number(entry.hours);
    if (!Number.isFinite(value)) continue;
    byDate.set(entry.date, (byDate.get(entry.date) || 0) + value);
  }
  return [...byDate.entries()]
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
    .map(([, hours]) => Math.round(hours * 100) / 100);
}

// Compact mini-bar strip of per-day hours; omitted entirely when no data.
function DailyHoursStrip({ entries, tone = 'blue' }) {
  const series = dailyHoursSeries(entries);
  if (series.length < 2) return null;
  const total = sumHours(entries);
  return (
    <div className="ts-daybars">
      <div className="ts-daybars__head">
        <span className="ts-daybars__title">Hours by day</span>
        <span className="ts-daybars__total">{fmtHours(total)} hrs</span>
      </div>
      <TrendBars values={series} tone={tone} ariaLabel="Hours logged per day" />
    </div>
  );
}

export function TimesheetHomePage() {
  const { user } = useCurrentUser();
  usePageMetadata({
    title: 'Timesheet Home',
    subtitle: 'Current weekly submission plus recent review outcomes and approval posture.',
  });
  const timesheets = useApi('/timesheets');
  const entries = useApi('/time-entries');
  const startMut = useMutation();

  async function startWeek() {
    try {
      await startMut.run(() => api.post('/timesheets', { anchorDate: todayDateOnly() }));
      timesheets.refresh();
    } catch (error) {
      toast.error({ title: 'Could not start this week', body: error.message });
    }
  }

  if (timesheets.loading) {
    return (
      <>
        <PageHeader
          eyebrow="OWHA Time Operations"
          title="Timesheet Home"
          subtitle="Submit the current week, correct rejected entries, and review the recent approval queue."
        />
        <KpiBandSkeleton count={3} />
        <LoadingSkeleton rows={6} />
      </>
    );
  }
  if (timesheets.error) return <ErrorState error={timesheets.error} />;

  const rows = sortTimesheetsByPeriodDescending(timesheets.data?.data || []);
  const visibleRows = user?.id ? rows.filter((row) => !row.userId || row.userId === user.id) : rows;
  const current = pickCurrentTimesheet(rows, user?.id);
  // The "working" current week is an OPEN (draft/resubmitted) sheet. A closed week
  // (approved/submitted/rejected) — including an older one — is not the active week: it belongs in
  // the review queue, and the "Start this week" affordance must appear so the user can begin the
  // actual current week instead of seeing a stale, all-"completed" strip.
  const openCurrent =
    current && ['draft', 'resubmitted'].includes(String(current.status).toLowerCase())
      ? current
      : null;
  const reviewQueue = visibleRows.filter((row) => row.id !== openCurrent?.id);

  const allEntries = entries.data?.data || [];
  const currentEntries = openCurrent ? entriesForTimesheet(allEntries, openCurrent.id) : [];
  const currentHours = sumHours(currentEntries);
  const approvedHours = sumHours(
    visibleRows
      .filter((row) => String(row.status).toLowerCase() === 'approved')
      .flatMap((row) => entriesForTimesheet(allEntries, row.id)),
  );
  const pendingCount = visibleRows.filter((row) =>
    ['submitted', 'resubmitted'].includes(String(row.status).toLowerCase()),
  ).length;

  const segments = approvalSegments(visibleRows);
  const checklistSteps = deriveThisWeekChecklist({
    current: openCurrent,
    entryCount: currentEntries.length,
  });

  const kpiItems = [
    {
      id: 'hours-logged',
      label: 'Hours logged (current week)',
      value: fmtHours(currentHours),
      unit: 'hrs',
      icon: '🕒',
      status: openCurrent ? statusTone(openCurrent.status) : 'slate',
      statusLabel: openCurrent ? <StatusChip status={openCurrent.status} /> : 'No timesheet',
      footer: openCurrent
        ? `${openCurrent.periodStart} to ${openCurrent.periodEnd || '—'}`
        : 'Awaiting weekly timesheet',
    },
    {
      id: 'hours-approved',
      label: 'Hours approved',
      value: fmtHours(approvedHours),
      unit: 'hrs',
      icon: '✅',
      status: approvedHours > 0 ? 'sage' : 'slate',
      statusLabel: approvedHours > 0 ? 'Cleared' : 'None',
      footer: 'Across approved weekly records',
    },
    {
      id: 'pending-review',
      label: 'Awaiting approval',
      value: pendingCount,
      icon: '⏳',
      status: pendingCount > 0 ? 'amber' : 'sage',
      statusLabel: pendingCount > 0 ? 'In review' : 'Clear',
      footer: pendingCount > 0 ? 'Submitted / resubmitted weeks' : 'No open approvals',
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="OWHA Time Operations"
        title="Timesheet Home"
        subtitle="Submit the current week, correct rejected entries, and review the recent approval queue."
        actions={
          openCurrent ? undefined : (
            <button
              type="button"
              className="btn primary"
              onClick={startWeek}
              disabled={startMut.running}
            >
              Start this week
            </button>
          )
        }
      />

      <ThisWeekStrip
        steps={checklistSteps}
        periodLabel={
          openCurrent ? `${openCurrent.periodStart} to ${openCurrent.periodEnd || '—'}` : null
        }
      />

      <KpiHeroBand eyebrow="Time posture" asOf="Live" items={kpiItems} columns={3} />

      <section className="ts-bento">
        <article className="time-panel">
          <div className="ts-panel-head">
            <h3>Current weekly timesheet</h3>
            <Link to="/timesheets/current">Open current week</Link>
          </div>
          {openCurrent ? (
            <div className="time-card ts-current">
              <div className="ts-current__top">
                <strong className="ts-current__id">{openCurrent.id}</strong>
                <StatusChip status={openCurrent.status} />
              </div>
              <p className="muted">
                {openCurrent.periodStart} to {openCurrent.periodEnd || '—'}
              </p>
              <div className="ts-current__hours">
                <span className="ts-current__hours-label">Hours logged</span>
                <InlineScoreCell
                  value={Number(fmtHours(currentHours))}
                  max={40}
                  tone={statusTone(openCurrent.status)}
                  suffix=" hrs"
                  ariaLabel={`${fmtHours(currentHours)} hours logged toward a 40-hour week`}
                />
              </div>
              <DailyHoursStrip entries={currentEntries} tone={statusTone(openCurrent.status)} />
            </div>
          ) : (
            <BrandedEmptyState
              icon="🗓️"
              eyebrow="OWHA Time Operations"
              title="No current timesheet"
              description="Start this week's record to begin logging labor hours."
              tone="slate"
              action={
                <button
                  type="button"
                  className="btn primary"
                  onClick={startWeek}
                  disabled={startMut.running}
                >
                  Start this week
                </button>
              }
            />
          )}
        </article>

        <article className="time-panel">
          <div className="ts-panel-head">
            <h3>Approval status</h3>
            <span className="chip outline sm">{reviewQueue.length}</span>
          </div>
          {visibleRows.length ? (
            <div className="ts-approval">
              <StatusDonutMini
                segments={segments}
                size={108}
                totalLabel="Weeks"
                ariaLabel="Approval status distribution"
              />
              <div className="ts-approval__meta">
                <span className="ts-panel-sub">Recent weekly records</span>
                <span className="ts-approval__line">
                  <strong>{pendingCount}</strong> awaiting approval
                </span>
                <span className="ts-approval__line">
                  <strong>{fmtHours(approvedHours)}</strong> approved hours
                </span>
              </div>
            </div>
          ) : (
            <p className="muted">No weekly records to summarize yet.</p>
          )}
          {/* W-6 / AC-TIME-5.1: the timesheet side of the labor-cost chain — approved
              hours visibly feed the financial posture labor card (and link there). */}
          <p className="muted small ts-w6-note">
            Your approved hours feed portfolio financials.{' '}
            <Link to="/financials">Open financial posture</Link>
          </p>

          <div className="ts-panel-head" style={{ marginTop: 20 }}>
            <h3>Review queue</h3>
          </div>
          {reviewQueue.length === 0 ? (
            <BrandedEmptyState
              icon="🎉"
              eyebrow="OWHA Time Operations"
              title="No review items"
              description="Rejected, submitted, and approved timesheets will appear here."
              tone="sage"
            />
          ) : (
            <div className="time-stack">
              {reviewQueue.map((timesheet) => (
                <div
                  key={timesheet.id}
                  className="time-card ts-queue-row"
                  data-tone={statusTone(timesheet.status)}
                >
                  <div className="ts-queue-row__top">
                    <Link to={`/timesheets/${timesheet.id}`}>{timesheet.id}</Link>
                    <StatusChip status={timesheet.status} />
                  </div>
                  <p className="muted">
                    {timesheet.periodStart} ·{' '}
                    <PersonLabel userId={timesheet.userId} fallback="current user" />
                  </p>
                  {timesheet.rejectionReason ? (
                    <p className="ts-queue-row__reason">{timesheet.rejectionReason}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

// AC-TIME-1.2 · time-entry categories offered by the inline weekly grid.
const TIME_CATEGORIES = ['delivery', 'collaboration', 'planning', 'support', 'other'];

// Weekday label for a YYYY-MM-DD date ("Mon 2026-06-15") without TZ drift.
function dayOptionLabel(date) {
  const weekday = new Date(`${date}T00:00:00.000Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  return `${weekday} ${date}`;
}

export function CurrentTimesheetPage() {
  const { user } = useCurrentUser();
  usePageMetadata({
    title: 'Current Weekly Timesheet',
    subtitle:
      'Weekly grid for logging time against projects, work items, actions, and meeting-derived drafts.',
  });
  const timesheets = useApi('/timesheets');
  const entries = useApi('/time-entries');
  // Target pickers (AC-TIME-1.2): my projects / work items / actions / meetings.
  // These also resolve target ids to display names for the grid's linked cells.
  const projectsRes = useApi('/projects', { query: { limit: 100 } });
  const workItemsRes = useApi('/work-items', { query: { limit: 100 } });
  const actionsRes = useApi('/actions', { query: { limit: 100 } });
  const meetingsRes = useApi('/meetings', { query: { limit: 100 } });
  const mutation = useMutation();
  const startMut = useMutation();
  const entryMut = useMutation();
  const [addDate, setAddDate] = useState('');
  const [addTarget, setAddTarget] = useState('');
  const [addHours, setAddHours] = useState(1);
  const [addCategory, setAddCategory] = useState('delivery');
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editHours, setEditHours] = useState(1);
  const [editCategory, setEditCategory] = useState('delivery');

  if (timesheets.loading || entries.loading) return <LoadingSkeleton rows={6} />;
  if (timesheets.error) return <ErrorState error={timesheets.error} />;
  if (entries.error) return <ErrorState error={entries.error} />;

  // targetKey "type:id" -> display name, across the four pickable record types.
  const targetGroups = [
    { type: 'project', label: 'Project', rows: projectsRes.data?.data || [] },
    { type: 'work_item', label: 'Work item', rows: workItemsRes.data?.data || [] },
    { type: 'action', label: 'Action', rows: actionsRes.data?.data || [] },
    { type: 'meeting', label: 'Meeting', rows: meetingsRes.data?.data || [] },
  ];
  const targetNameByKey = new Map();
  const targetOptions = [];
  for (const group of targetGroups) {
    for (const row of group.rows) {
      if (!row?.id) continue;
      const name = row.name || row.title || row.id;
      targetNameByKey.set(`${group.type}:${row.id}`, name);
      targetOptions.push({ value: `${group.type}:${row.id}`, label: `${group.label} · ${name}` });
    }
  }
  const targetNameFor = (entry) =>
    targetNameByKey.get(`${entry.targetEntityType}:${entry.targetEntityId}`) ||
    entry.targetEntityId;

  // Treat only an OPEN (draft/resubmitted) sheet as the working current week. A closed/old week
  // (approved/submitted/rejected) must not occupy this page — instead surface "Start this week" so
  // the user can begin the actual current week. Closed weeks remain reachable via the detail route.
  const latestTimesheet = pickCurrentTimesheet(timesheets.data?.data || [], user?.id);
  const current =
    latestTimesheet &&
    ['draft', 'resubmitted'].includes(String(latestTimesheet.status).toLowerCase())
      ? latestTimesheet
      : null;
  const rows = current ? entriesForTimesheet(entries.data?.data || [], current.id) : [];
  const totalHours = sumHours(rows);
  const dayOptions = current ? enumerateWeekDays(current.periodStart, current.periodEnd) : [];

  async function addEntry(event) {
    event.preventDefault();
    if (!current || !addTarget) return;
    const [targetEntityType, targetEntityId] = addTarget.split(':');
    const date = addDate || dayOptions[0] || todayDateOnly();
    try {
      await entryMut.run(() =>
        api.post('/time-entries', {
          date,
          hours: Number(addHours || 0),
          category: addCategory,
          targetEntityType,
          targetEntityId,
          timesheetId: current.id,
        }),
      );
      toast.success({
        title: 'Time entry added',
        body: `${fmtHours(Number(addHours || 0))} hrs on ${date} · ${targetNameByKey.get(addTarget) || targetEntityId}`,
      });
      setAddTarget('');
      setAddHours(1);
      entries.refresh();
    } catch (error) {
      toast.error({ title: 'Could not add time entry', body: error.message });
    }
  }

  function beginEdit(entry) {
    setEditingId(entry.id);
    setEditDate(entry.date);
    setEditHours(Number(entry.hours) || 0);
    setEditCategory(entry.category || 'delivery');
  }

  async function saveEdit(entry) {
    try {
      await entryMut.run(() =>
        api.patch(`/time-entries/${entry.id}`, {
          date: editDate || entry.date,
          hours: Number(editHours || 0),
          category: editCategory,
        }),
      );
      toast.success({ title: 'Time entry updated', body: entry.id });
      setEditingId(null);
      entries.refresh();
    } catch (error) {
      toast.error({ title: 'Could not update time entry', body: error.message });
    }
  }

  async function deleteEntry(entry) {
    try {
      await entryMut.run(() => api.delete(`/time-entries/${entry.id}`));
      toast.success({ title: 'Time entry deleted', body: entry.id });
      if (editingId === entry.id) setEditingId(null);
      entries.refresh();
    } catch (error) {
      toast.error({ title: 'Could not delete time entry', body: error.message });
    }
  }

  async function submitTimesheet() {
    if (!current) return;
    try {
      await mutation.run(() => api.post(`/timesheets/${current.id}/submit`, {}));
      toast.success({
        title: 'Timesheet submitted',
        body: `${current.id} routed for approval with ${fmtHours(totalHours)} hrs.`,
      });
      timesheets.refresh();
    } catch (error) {
      toast.error({ title: 'Could not submit timesheet', body: error.message });
    }
  }

  async function startWeek() {
    try {
      await startMut.run(() => api.post('/timesheets', { anchorDate: todayDateOnly() }));
      timesheets.refresh();
    } catch (error) {
      toast.error({ title: 'Could not start this week', body: error.message });
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="OWHA Time Operations"
        title="Current Weekly Timesheet"
        subtitle={
          current
            ? `${current.periodStart} to ${current.periodEnd || '—'}`
            : 'Open or create a current-week timesheet to submit labor hours.'
        }
        actions={
          <div className="row" style={{ gap: 8 }}>
            {current ? (
              <button
                type="button"
                className="btn primary"
                onClick={submitTimesheet}
                disabled={mutation.running || rows.length === 0}
              >
                {mutation.running ? 'Submitting…' : 'Submit timesheet'}
              </button>
            ) : (
              <button
                type="button"
                className="btn primary"
                onClick={startWeek}
                disabled={startMut.running}
              >
                Start this week
              </button>
            )}
          </div>
        }
      />
      {current && rows.length ? (
        <KpiHeroBand
          eyebrow="This week"
          items={[
            {
              id: 'week-hours',
              label: 'Hours logged',
              value: fmtHours(totalHours),
              unit: 'hrs',
              icon: '🕒',
              status: statusTone(current.status),
              statusLabel: <StatusChip status={current.status} />,
              footer: `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`,
            },
            {
              id: 'week-entries',
              label: 'Time entries',
              value: rows.length,
              icon: '📋',
              status: 'blue',
              footer: 'Lines on this weekly record',
            },
            {
              id: 'week-target',
              label: 'Weekly target',
              value: 40,
              unit: 'hrs',
              icon: '🎯',
              status: totalHours >= 40 ? 'sage' : totalHours >= 32 ? 'amber' : 'slate',
              statusLabel: `${Math.round((totalHours / 40) * 100)}%`,
              footer: 'Standard full-week baseline',
            },
          ]}
          columns={3}
        />
      ) : null}
      <section className="time-panel">
        {current ? (
          <>
            {rows.length ? (
              <>
                <div className="ts-panel-head">
                  <h3>Weekly entries</h3>
                  <span className="ts-panel-sub">{fmtHours(totalHours)} hrs total</span>
                </div>
                <DailyHoursStrip entries={rows} tone={statusTone(current.status)} />
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Target</th>
                      <th>Hours</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>
                        <span className="ts-sr-only">Row actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((entry) => {
                      const targetRoute = timeTargetRoute(
                        entry.targetEntityType,
                        entry.targetEntityId,
                      );
                      const targetName = targetNameFor(entry);
                      if (editingId === entry.id) {
                        return (
                          <tr key={entry.id} className="ts-grid-edit-row">
                            <td>
                              <label className="ts-sr-only" htmlFor={`ts-edit-date-${entry.id}`}>
                                Day for {entry.id}
                              </label>
                              <Select
                                id={`ts-edit-date-${entry.id}`}
                                value={editDate}
                                onChange={setEditDate}
                                options={dayOptions.map((day) => ({
                                  value: day,
                                  label: dayOptionLabel(day),
                                }))}
                              />
                            </td>
                            <td>
                              {targetRoute ? (
                                <Link to={targetRoute}>{targetName}</Link>
                              ) : (
                                targetName
                              )}
                            </td>
                            <td>
                              <label className="ts-sr-only" htmlFor={`ts-edit-hours-${entry.id}`}>
                                Hours for {entry.id}
                              </label>
                              <NumberInput
                                id={`ts-edit-hours-${entry.id}`}
                                value={editHours}
                                onChange={setEditHours}
                                min={0.25}
                                step={0.25}
                              />
                            </td>
                            <td>
                              <label
                                className="ts-sr-only"
                                htmlFor={`ts-edit-category-${entry.id}`}
                              >
                                Category for {entry.id}
                              </label>
                              <Select
                                id={`ts-edit-category-${entry.id}`}
                                value={editCategory}
                                onChange={setEditCategory}
                                options={TIME_CATEGORIES}
                              />
                            </td>
                            <td>
                              <StatusChip status={entry.status} />
                            </td>
                            <td className="ts-grid-actions">
                              <button
                                type="button"
                                className="btn primary sm"
                                onClick={() => saveEdit(entry)}
                                disabled={entryMut.running}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() => setEditingId(null)}
                              >
                                Cancel
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={entry.id}>
                          <td>{entry.date}</td>
                          <td>
                            {targetRoute ? <Link to={targetRoute}>{targetName}</Link> : targetName}
                          </td>
                          <td className="ts-hours-cell">
                            <InlineScoreCell
                              value={Number(entry.hours) || 0}
                              max={12}
                              tone={statusTone(entry.status)}
                              suffix=" h"
                              ariaLabel={`${entry.hours} hours`}
                            />
                          </td>
                          <td>{entry.category || '—'}</td>
                          <td>
                            <StatusChip status={entry.status} />
                          </td>
                          <td className="ts-grid-actions">
                            <button
                              type="button"
                              className="btn ghost sm"
                              onClick={() => beginEdit(entry)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn ghost sm ts-grid-delete"
                              onClick={() => deleteEntry(entry)}
                              disabled={entryMut.running}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              <BrandedEmptyState
                icon="📝"
                eyebrow="OWHA Time Operations"
                title="No time entries"
                description="Add an entry below, or convert a meeting into draft entries before submission."
                tone="slate"
              />
            )}

            {/* AC-TIME-1.2 — inline add row: day × target picker × hours × category. */}
            <form className="ts-grid-add" onSubmit={addEntry}>
              <div className="ts-panel-head">
                <h3>Add entry</h3>
                <span className="ts-panel-sub muted">
                  Targets cover my projects, work items, actions, and meetings
                </span>
              </div>
              <div className="ts-grid-add__fields">
                <FormField label="Day" htmlFor="ts-add-day">
                  <Select
                    id="ts-add-day"
                    value={addDate || dayOptions[0] || ''}
                    onChange={setAddDate}
                    options={dayOptions.map((day) => ({
                      value: day,
                      label: dayOptionLabel(day),
                    }))}
                  />
                </FormField>
                <FormField label="Target" htmlFor="ts-add-target">
                  <Select
                    id="ts-add-target"
                    value={addTarget}
                    onChange={setAddTarget}
                    options={targetOptions}
                    placeholder="Select a target"
                  />
                </FormField>
                <FormField label="Hours" htmlFor="ts-add-hours">
                  <NumberInput
                    id="ts-add-hours"
                    value={addHours}
                    onChange={setAddHours}
                    min={0.25}
                    step={0.25}
                  />
                </FormField>
                <FormField label="Category" htmlFor="ts-add-category">
                  <Select
                    id="ts-add-category"
                    value={addCategory}
                    onChange={setAddCategory}
                    options={TIME_CATEGORIES}
                  />
                </FormField>
                <button
                  type="submit"
                  className="btn primary"
                  disabled={entryMut.running || !addTarget}
                >
                  Add entry
                </button>
              </div>
            </form>
          </>
        ) : (
          <BrandedEmptyState
            icon="🗓️"
            eyebrow="OWHA Time Operations"
            title="No current timesheet"
            description="Start this week's record to begin logging labor hours."
            tone="slate"
            action={
              <button
                type="button"
                className="btn primary"
                onClick={startWeek}
                disabled={startMut.running}
              >
                Start this week
              </button>
            }
          />
        )}
      </section>
    </>
  );
}

export function TimesheetDetailPage() {
  const { id } = useParams();
  const { user, roles } = useCurrentUser();
  usePageMetadata({
    title: 'Timesheet Detail',
    subtitle: 'Review approval posture, rejection rationale, and the line-level weekly record.',
  });
  const timesheet = useApi(`/timesheets/${id}`, { skip: !id });
  const entries = useApi('/time-entries');
  const approveMut = useMutation();
  const rejectMut = useMutation();
  const resubmitMut = useMutation();
  const [rejectionRationale, setRejectionRationale] = useState('');

  if (timesheet.loading || entries.loading) return <LoadingSkeleton rows={6} />;
  if (timesheet.error?.status === 404) return <EmptyState title="Timesheet not found" />;
  if (timesheet.error) return <ErrorState error={timesheet.error} />;
  if (entries.error) return <ErrorState error={entries.error} />;

  const data = timesheet.data?.data || null;
  const rows = data ? entriesForTimesheet(entries.data?.data || [], data.id) : [];

  async function approve() {
    if (!data) return;
    try {
      await approveMut.run(() => api.post(`/timesheets/${data.id}/approve`, {}));
      toast.success({ title: 'Timesheet approved', body: `${data.id} marked approved.` });
      timesheet.refresh();
    } catch (error) {
      toast.error({ title: 'Could not approve timesheet', body: error.message });
    }
  }

  async function resubmit() {
    if (!data) return;
    try {
      await resubmitMut.run(() => api.post(`/timesheets/${data.id}/resubmit`, {}));
      toast.success({ title: 'Timesheet resubmitted', body: `${data.id} returned to the queue.` });
      timesheet.refresh();
    } catch (error) {
      toast.error({ title: 'Could not resubmit timesheet', body: error.message });
    }
  }

  async function reject() {
    if (!data || !rejectionRationale.trim()) return;
    try {
      await rejectMut.run(() =>
        api.post(`/timesheets/${data.id}/reject`, {
          rationale: rejectionRationale.trim(),
        }),
      );
      toast.success({ title: 'Timesheet rejected', body: 'Returned to submitter for correction.' });
      timesheet.refresh();
    } catch (error) {
      toast.error({ title: 'Could not reject timesheet', body: error.message });
    }
  }

  if (!data) return <EmptyState title="Timesheet not found" />;

  // Role- and state-aware controls (SM-01 / AC-TIME-2.1, AC-ROLE-1):
  // approve/reject only for approver roles on an actionable submission;
  // resubmit only for the submitting user on a rejected sheet.
  const anyRunning = approveMut.running || rejectMut.running || resubmitMut.running;
  const status = String(data.status || '').toLowerCase();
  const isApprover = rbac.hasAnyRole(roles, rbac.TIME_APPROVER_ROLES);
  const isSubmitter = !data.userId || data.userId === user?.id;
  const actionable = ['submitted', 'resubmitted'].includes(status);
  const canApprove = isApprover && actionable;
  const canReject = isApprover && actionable;
  const canResubmit = isSubmitter && status === 'rejected';
  const currentRationale = rejectionRationale || data.rejectionReason || '';
  const totalHours = sumHours(rows);
  const tone = statusTone(data.status);

  return (
    <>
      <PageHeader
        eyebrow="OWHA Time Operations"
        title="Timesheet Detail"
        subtitle={`${data.id} · ${data.periodStart || '—'} to ${data.periodEnd || '—'}`}
        actions={
          <div className="row" style={{ gap: 8 }}>
            {canResubmit ? (
              <button type="button" className="btn ghost" onClick={resubmit} disabled={anyRunning}>
                {resubmitMut.running ? 'Resubmitting…' : 'Resubmit timesheet'}
              </button>
            ) : null}
            {canReject ? (
              <button
                type="button"
                className="btn outline"
                onClick={reject}
                disabled={anyRunning || !rejectionRationale.trim()}
              >
                {rejectMut.running ? 'Rejecting…' : 'Reject timesheet'}
              </button>
            ) : null}
            {canApprove ? (
              <button type="button" className="btn primary" onClick={approve} disabled={anyRunning}>
                {approveMut.running ? 'Approving…' : 'Approve timesheet'}
              </button>
            ) : null}
          </div>
        }
      />

      <KpiHeroBand
        eyebrow="Weekly record"
        items={[
          {
            id: 'detail-hours',
            label: 'Hours on record',
            value: fmtHours(totalHours),
            unit: 'hrs',
            icon: '🕒',
            status: tone,
            statusLabel: <StatusChip status={data.status} />,
            footer: `${rows.length} ${rows.length === 1 ? 'line' : 'lines'}`,
          },
          {
            id: 'detail-status',
            label: 'Approval posture',
            value:
              String(data.status || '')
                .charAt(0)
                .toUpperCase() + String(data.status || '').slice(1),
            icon: tone === 'sage' ? '✅' : tone === 'brick' ? '⚠️' : '⏳',
            status: tone,
            footer: data.rejectionReason ? 'Returned for correction' : 'Current submission state',
          },
        ]}
        columns={2}
      />

      <section className="ts-bento">
        <article className="time-panel">
          <div className="ts-panel-head">
            <h3>Submission posture</h3>
            <StatusChip status={data.status} />
          </div>
          {data.approvedByUserId ? (
            <p className="muted" data-testid="timesheet-approver-identity">
              Approved by{' '}
              <strong>
                <PersonLabel userId={data.approvedByUserId} />
              </strong>
              {data.approvedAt ? ` · ${new Date(data.approvedAt).toLocaleString()}` : ''}
            </p>
          ) : null}
          {data.rejectionReason ? (
            <p className="ts-queue-row__reason">{data.rejectionReason}</p>
          ) : (
            <p className="muted">No rejection rationale recorded.</p>
          )}
          {canReject ? (
            <div className="time-form" style={{ marginTop: 16 }}>
              <FormField
                label="Rejection rationale"
                htmlFor="timesheet-rejection-rationale"
                hint="Required when returning a submitted timesheet for correction."
              >
                <Textarea
                  id="timesheet-rejection-rationale"
                  rows={3}
                  value={currentRationale}
                  onChange={setRejectionRationale}
                  placeholder="Explain what needs to be corrected before approval."
                />
              </FormField>
            </div>
          ) : null}
        </article>
        <article className="time-panel">
          {rows.length === 0 ? (
            <BrandedEmptyState
              icon="📝"
              eyebrow="OWHA Time Operations"
              title="No time entries"
              description="This weekly record does not have any attached time lines."
              tone="slate"
            />
          ) : (
            <>
              <div className="ts-panel-head">
                <h3>Line-level record</h3>
                <span className="ts-panel-sub">{fmtHours(totalHours)} hrs total</span>
              </div>
              <DailyHoursStrip entries={rows} tone={tone} />
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Target</th>
                    <th>Hours</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.date}</td>
                      <td>{entry.targetEntityId}</td>
                      <td className="ts-hours-cell">
                        <InlineScoreCell
                          value={Number(entry.hours) || 0}
                          max={12}
                          tone={statusTone(entry.status)}
                          suffix=" h"
                          ariaLabel={`${entry.hours} hours`}
                        />
                      </td>
                      <td>
                        <StatusChip status={entry.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </article>
      </section>
    </>
  );
}

export function LogTimePage() {
  const { id } = useParams();
  const { user } = useCurrentUser();
  const timesheets = useApi('/timesheets');
  const workItem = useApi(id ? `/work-items/${id}` : null, { skip: !id });
  const mutation = useMutation();
  const [date, setDate] = useState(todayDateOnly());
  const [hours, setHours] = useState(1);
  const [notes, setNotes] = useState('');
  const [savedEntryId, setSavedEntryId] = useState('');

  usePageMetadata({
    title: 'Log Time',
    subtitle: 'Quick time entry against a work item or action without leaving the delivery flow.',
  });

  if (timesheets.loading || workItem.loading) return <LoadingSkeleton rows={5} />;
  if (timesheets.error) return <ErrorState error={timesheets.error} />;
  if (workItem.error?.status === 404) return <EmptyState title="Work item not found" />;
  if (workItem.error) return <ErrorState error={workItem.error} />;

  const targetWorkItem = workItem.data?.data || null;
  // The fetched weekly sheet that actually covers the selected date is the one the save writes
  // to, so the legend below and the persisted destination stay in agreement even when the
  // "current" pick is a different week (F-G04-001).
  const coveringTimesheet = pickCoveringTimesheet(timesheets.data?.data || [], date, user?.id);

  async function submit(event) {
    event.preventDefault();
    // Create-then-continue: reuse the fetched sheet that covers the entry date (any covering
    // sheet, not only the current pick); otherwise start the week anchored on the entry date
    // (server is find-or-create) so the entry never lands in a stale prior-week draft. Both calls
    // run in one mutation so Save stays disabled across them.
    const reusableId = coveringTimesheet ? coveringTimesheet.id : null;
    try {
      const response = await mutation.run(async () => {
        let timesheetId = reusableId;
        if (!timesheetId) {
          const created = await api.post('/timesheets', { anchorDate: date });
          timesheetId = created?.data?.id || created?.data?.data?.id;
        }
        return api.post('/time-entries', {
          date,
          hours: Number(hours || 0),
          notes,
          targetEntityType: 'work_item',
          targetEntityId: id,
          projectId: targetWorkItem?.projectId || null,
          workItemId: id,
          category: 'delivery',
          timesheetId,
        });
      });
      timesheets.refresh?.();
      setSavedEntryId(response?.data?.id || response?.data?.data?.id || '');
      // Reset effort fields so a second Save is intentional, not an accidental duplicate entry.
      setHours(1);
      setNotes('');
      toast.success({ title: 'Time entry saved', body: targetWorkItem.title || id });
    } catch (error) {
      toast.error({ title: 'Could not save time entry', body: error.message });
    }
  }

  if (!targetWorkItem) return <EmptyState title="Work item not found" />;

  return (
    <>
      <PageHeader
        eyebrow="OWHA Time Operations"
        title="Log Time"
        subtitle={`Quick-entry route for work item ${id || 'unknown'}.`}
      />
      <form className="time-panel time-form ts-form-shell" onSubmit={submit}>
        <div className="time-card ts-target-card">
          <strong>{targetWorkItem.title || id}</strong>
          <p className="muted">
            {coveringTimesheet
              ? `Saving into weekly timesheet ${coveringTimesheet.id}`
              : 'A weekly timesheet will be started automatically for the selected date.'}
          </p>
        </div>
        <FormField label="Date" htmlFor="time-date">
          <DateInput id="time-date" value={date} onChange={setDate} />
        </FormField>
        <FormField label="Hours" htmlFor="time-hours">
          <NumberInput id="time-hours" value={hours} onChange={setHours} min={0.25} step={0.25} />
        </FormField>
        <FormField label="Notes" htmlFor="time-notes">
          <TextInput id="time-notes" value={notes} onChange={setNotes} />
        </FormField>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="submit" className="btn primary" disabled={mutation.running}>
            Save time entry
          </button>
        </div>
        {savedEntryId ? (
          <span className="ts-saved-flash" role="status">
            <span className="ts-saved-flash__dot" aria-hidden />
            <span className="muted">Saved {savedEntryId}</span>
            <Link to="/timesheets/current">View this week</Link>
          </span>
        ) : null}
      </form>
    </>
  );
}

export function MeetingConvertToTimePage() {
  const { id } = useParams();
  const { nameOf } = usePeople();
  const meeting = useApi(`/meetings/${id}`, { skip: !id });
  const mutation = useMutation();
  const [drafts, setDrafts] = useState([]);

  usePageMetadata({
    title: 'Meeting to Time',
    subtitle: 'Convert meeting attendance into draft time entries for weekly review.',
  });

  if (meeting.loading) return <LoadingSkeleton rows={5} />;
  if (meeting.error?.status === 404) return <EmptyState title="Meeting not found" />;
  if (meeting.error) return <ErrorState error={meeting.error} />;
  const data = meeting.data?.data || null;
  if (!data) return <EmptyState title="Meeting not found" />;

  async function createDrafts() {
    try {
      const response = await mutation.run(() =>
        api.post(`/meetings/${data.id}/time-drafts`, { suggestedHours: 1 }),
      );
      const list = response?.data?.drafts || response?.data?.data?.drafts || [];
      setDrafts(list);
      toast.success({
        title: 'Draft time entries created',
        body: `${list.length} draft${list.length === 1 ? '' : 's'} suggested from attendance.`,
      });
    } catch (error) {
      toast.error({ title: 'Could not create draft entries', body: error.message });
    }
  }

  const attendees = data.attendees || [];
  const draftHours = drafts.reduce((total, d) => total + (Number(d.suggestedHours) || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow="OWHA Time Operations"
        title="Meeting to Time"
        subtitle={data.title || data.id}
        actions={
          <button
            type="button"
            className="btn primary"
            onClick={createDrafts}
            disabled={mutation.running}
          >
            Create draft time entries
          </button>
        }
      />
      <section className="ts-bento">
        <article className="time-panel">
          <div className="ts-panel-head">
            <h3>Attendees</h3>
            <span className="chip outline sm">{attendees.length}</span>
          </div>
          {attendees.length ? (
            <ul className="time-list ts-people-list">
              {attendees.map((attendee) => (
                <li key={attendee}>
                  <span className="ts-people-avatar" aria-hidden>
                    {String(nameOf(attendee))
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(-2)
                      .toUpperCase() || '··'}
                  </span>
                  <span>
                    <PersonLabel userId={attendee} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <BrandedEmptyState
              icon="👥"
              eyebrow="OWHA Time Operations"
              title="No attendees"
              description="Meeting attendance is required for time-draft conversion."
              tone="slate"
            />
          )}
        </article>
        <article className="time-panel">
          <div className="ts-panel-head">
            <h3>Draft time entries</h3>
            {drafts.length ? (
              <span className="ts-panel-sub">{fmtHours(draftHours)} hrs drafted</span>
            ) : null}
          </div>
          {drafts.length ? (
            <ul className="time-list ts-draft-list">
              {drafts.map((draft) => (
                <li key={draft.id}>
                  <span className="ts-people-avatar" aria-hidden>
                    {String(nameOf(draft.userId))
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(-2)
                      .toUpperCase() || '··'}
                  </span>
                  <span>
                    <strong>
                      <PersonLabel userId={draft.userId} />
                    </strong>{' '}
                    · {draft.suggestedHours} hours
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">No draft time entries created yet.</p>
          )}
        </article>
      </section>
    </>
  );
}
